/**
 * Help desk response targets (issue #1096): when Meridian owes a reply.
 *
 *   Normal: by the end of the next business day (5 pm Central on the first
 *           business day after the day the request came in).
 *   Urgent: within 4 business hours of the moment it became urgent (filed
 *           urgent, or marked urgent later by the company's administrator).
 *
 * Business hours: Monday to Friday, 8 am to 5 pm US Central
 * (America/Chicago, daylight saving included), US federal holidays excluded
 * on their observed dates.
 *
 * The droplet computes the same thing for Meridian's email
 * (src/meridian_pipeline/api/helpdesk_hours.py). Both are checked against
 * tests/fixtures/help_desk_reply_by.json so they cannot drift.
 */

export const TIME_ZONE = "America/Chicago";
export const OPENS_HOUR = 8;
export const CLOSES_HOUR = 17;
export const URGENT_HOURS = 4;

const HOUR_MS = 3600 * 1000;
const DAY_MS = 24 * HOUR_MS;

const partsFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: TIME_ZONE,
  hourCycle: "h23",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

/** Central wall-clock parts of an instant. */
function central(ms) {
  const parts = {};
  for (const p of partsFormatter.formatToParts(new Date(ms))) parts[p.type] = p.value;
  return {
    y: Number(parts.year),
    m: Number(parts.month),
    d: Number(parts.day),
    h: Number(parts.hour),
    min: Number(parts.minute),
    s: Number(parts.second),
  };
}

/** The instant a Central wall-clock time names (business hours never sit in a DST gap). */
function fromCentral(y, m, d, h, min = 0) {
  const guess = Date.UTC(y, m - 1, d, h, min);
  let ms = guess;
  for (let i = 0; i < 2; i += 1) {
    const p = central(ms);
    const asUtc = Date.UTC(p.y, p.m - 1, p.d, p.h, p.min, p.s);
    ms += guess - asUtc;
  }
  return ms;
}

/** A calendar day as UTC-midnight ms; weekday 0 = Sunday. */
function day(y, m, d) {
  return Date.UTC(y, m - 1, d);
}

function ymd(ms) {
  const dt = new Date(ms);
  return { y: dt.getUTCFullYear(), m: dt.getUTCMonth() + 1, d: dt.getUTCDate() };
}

function nthWeekday(y, m, weekday, n) {
  if (n > 0) {
    const first = day(y, m, 1);
    const offset = (weekday - new Date(first).getUTCDay() + 7) % 7;
    return first + (offset + 7 * (n - 1)) * DAY_MS;
  }
  const last = day(y, m + 1, 1) - DAY_MS;
  const back = (new Date(last).getUTCDay() - weekday + 7) % 7;
  return last - back * DAY_MS;
}

function observed(ms) {
  const wd = new Date(ms).getUTCDay();
  if (wd === 6) return ms - DAY_MS;
  if (wd === 0) return ms + DAY_MS;
  return ms;
}

function actualHolidays(y) {
  return [
    observed(day(y, 1, 1)),
    nthWeekday(y, 1, 1, 3),
    nthWeekday(y, 2, 1, 3),
    nthWeekday(y, 5, 1, -1),
    observed(day(y, 6, 19)),
    observed(day(y, 7, 4)),
    nthWeekday(y, 9, 1, 1),
    nthWeekday(y, 10, 1, 2),
    observed(day(y, 11, 11)),
    nthWeekday(y, 11, 4, 4),
    observed(day(y, 12, 25)),
  ];
}

const holidayCache = new Map();

/** Observed US federal holidays in a year, as UTC-midnight ms. */
export function federalHolidays(y) {
  if (!holidayCache.has(y)) {
    const all = [...actualHolidays(y), ...actualHolidays(y + 1)];
    holidayCache.set(y, new Set(all.filter((ms) => ymd(ms).y === y)));
  }
  return holidayCache.get(y);
}

function isBusinessDay(dayMs) {
  const wd = new Date(dayMs).getUTCDay();
  return wd >= 1 && wd <= 5 && !federalHolidays(ymd(dayMs).y).has(dayMs);
}

function nextBusinessDay(dayMs) {
  let next = dayMs + DAY_MS;
  while (!isBusinessDay(next)) next += DAY_MS;
  return next;
}

function at(dayMs, hour) {
  const { y, m, d } = ymd(dayMs);
  return fromCentral(y, m, d, hour);
}

/** `startMs` plus `hours` of business time. */
export function addBusinessHours(startMs, hours) {
  let cur = startMs;
  let remaining = hours * HOUR_MS;
  for (;;) {
    const p = central(cur);
    const today = day(p.y, p.m, p.d);
    const clock = p.h + p.min / 60 + p.s / 3600;
    if (!isBusinessDay(today) || clock >= CLOSES_HOUR) {
      cur = at(nextBusinessDay(today), OPENS_HOUR);
      continue;
    }
    if (clock < OPENS_HOUR) cur = at(today, OPENS_HOUR);
    const leftToday = at(today, CLOSES_HOUR) - cur;
    if (remaining <= leftToday) return cur + remaining;
    remaining -= leftToday;
    cur = at(nextBusinessDay(today), OPENS_HOUR);
  }
}

function toMs(value) {
  if (value == null || value === "") return null;
  const ms = value instanceof Date ? value.getTime() : Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

/**
 * When Meridian owes the first reply, as a Date. `createdAt` and `urgentAt`
 * are ISO strings or Dates; an urgent request without `urgentAt` counts from
 * `createdAt`. Null when `createdAt` is missing or unreadable.
 */
export function replyBy(createdAt, urgency, urgentAt = null) {
  const created = toMs(createdAt);
  if (created == null) return null;
  if (urgency === "urgent") {
    return new Date(addBusinessHours(toMs(urgentAt) ?? created, URGENT_HOURS));
  }
  const p = central(created);
  return new Date(at(nextBusinessDay(day(p.y, p.m, p.d)), CLOSES_HOUR));
}

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** "Tue Oct 13, 5:00 pm CT": the reply-by time in Central, as shown everywhere. */
export function formatCentral(when) {
  const ms = toMs(when);
  if (ms == null) return "";
  const p = central(ms);
  const wd = new Date(day(p.y, p.m, p.d)).getUTCDay();
  const hour = p.h % 12 || 12;
  const ampm = p.h < 12 ? "am" : "pm";
  const min = String(p.min).padStart(2, "0");
  return `${DAY_NAMES[wd]} ${MONTH_NAMES[p.m - 1]} ${p.d}, ${hour}:${min} ${ampm} CT`;
}

/** The two targets, worded for the form and the My requests page. */
export const RESPONSE_TARGETS = Object.freeze({
  normal: "Reply by the end of the next business day",
  urgent: "Reply within 4 business hours",
});

export const BUSINESS_HOURS_NOTE =
  "Business hours are Monday to Friday, 8 am to 5 pm Central, except US federal holidays.";
